import { useEffect, useState } from "react";
import AdminNav from "../../components/AdminNav";
import {
  getMapCredentials,
  createMapCredential,
  updateMapCredential,
  deleteMapCredential,
  testMapCredential,
  activateMapCredential,
  getMapConfig,
  updateMapConfig,
  getMapHealth,
  getMapAuditLogs
} from "../../api/client";

export default function AdminMapProviders() {
  const [credentials, setCredentials] = useState([]);
  const [config, setConfig] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState("");

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: "",
    provider: "mapbox",
    credential: "",
    credentialType: "secret",
    environment: "production",
    notes: "",
    isActive: true
  });
  const [testingId, setTestingId] = useState(null);
  const [testResults, setTestResults] = useState({});

  const loadData = async () => {
    try {
      setLoading(true);
      const [credsRes, configRes, healthRes, logsRes] = await Promise.all([
        getMapCredentials(),
        getMapConfig(),
        getMapHealth(),
        getMapAuditLogs(25)
      ]);

      setCredentials(credsRes.data.credentials || []);
      setConfig(configRes.data.config || {});
      setHealthData(healthRes.data || null);
      setAuditLogs(logsRes.data.logs || []);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to load map provider settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleOpenAdd = () => {
    setEditingId(null);
    setForm({
      name: "",
      provider: "mapbox",
      credential: "",
      credentialType: "secret",
      environment: "production",
      notes: "",
      isActive: true
    });
    setShowModal(true);
  };

  const handleOpenEdit = (cred) => {
    setEditingId(cred._id);
    setForm({
      name: cred.name,
      provider: cred.provider,
      credential: "", // keep blank unless changing
      credentialType: cred.credentialType,
      environment: cred.environment,
      notes: cred.notes || "",
      isActive: cred.isActive,
      isEnabled: cred.isEnabled
    });
    setShowModal(true);
  };

  const handleSaveCredential = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        const payload = { ...form };
        if (!payload.credential) delete payload.credential;
        await updateMapCredential(editingId, payload);
        setMessage({ type: "success", text: "Credential updated successfully!" });
      } else {
        await createMapCredential(form);
        setMessage({ type: "success", text: "New map credential added successfully!" });
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.error || "Failed to save credential" });
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete credential "${name}"?`)) return;
    try {
      await deleteMapCredential(id);
      setMessage({ type: "success", text: `Credential "${name}" deleted` });
      loadData();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.error || "Failed to delete credential" });
    }
  };

  const handleTest = async (id) => {
    setTestingId(id);
    try {
      const res = await testMapCredential(id);
      setTestResults((prev) => ({ ...prev, [id]: res.data }));
      loadData();
    } catch (err) {
      setTestResults((prev) => ({
        ...prev,
        [id]: { ok: false, error: err.response?.data?.error || "Test request failed" }
      }));
    } finally {
      setTestingId(null);
    }
  };

  const handleActivate = async (id, name) => {
    try {
      await activateMapCredential(id);
      setMessage({ type: "success", text: `"${name}" is now the active credential for its provider` });
      loadData();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.error || "Failed to activate credential" });
    }
  };

  const handleServiceChange = (serviceKey, field, value) => {
    setConfig((prev) => ({
      ...prev,
      services: {
        ...prev.services,
        [serviceKey]: {
          ...(prev.services?.[serviceKey] || {}),
          [field]: value
        }
      }
    }));
  };

  const handleSaveConfig = async () => {
    try {
      setSavingConfig(true);
      await updateMapConfig(config);
      setMessage({ type: "success", text: "Service routing and failover settings saved!" });
      loadData();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.error || "Failed to save configuration" });
    } finally {
      setSavingConfig(false);
    }
  };

  const getStatusBadge = (health) => {
    const status = health?.status || "untested";
    const colors = {
      healthy: { bg: "rgba(16, 185, 129, 0.2)", text: "#10b981", label: "● Healthy" },
      degraded: { bg: "rgba(245, 158, 11, 0.2)", text: "#f59e0b", label: "▲ Degraded" },
      unhealthy: { bg: "rgba(239, 68, 68, 0.2)", text: "#ef4444", label: "✖ Unhealthy" },
      untested: { bg: "rgba(156, 163, 175, 0.2)", text: "#9ca3af", label: "○ Untested" }
    };
    const c = colors[status] || colors.untested;
    return (
      <span
        style={{
          display: "inline-block",
          padding: "3px 8px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: 600,
          background: c.bg,
          color: c.text
        }}
      >
        {c.label}
      </span>
    );
  };

  const servicesList = [
    { key: "distance", label: "Distance Calculation", desc: "Pricing engine pickup-to-dropoff road distance" },
    { key: "routing", label: "Route & Polyline", desc: "Turn-by-turn driving polyline and ETA duration" },
    { key: "addressSearch", label: "Address Search", desc: "Address input autocompletion & forward geocoding" },
    { key: "reverseGeocode", label: "Reverse Geocoding", desc: "Coordinate pin to formatted street address" },
    { key: "geocode", label: "Forward Geocoding", desc: "Direct address string to lat/lng lookup" }
  ];

  return (
    <div className="admin-app">
      <AdminNav />

      <div className="page wide page-with-nav" style={{ maxWidth: "1200px" }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "16px",
            marginBottom: "24px"
          }}
        >
          <div>
            <h1 style={{ margin: "0 0 6px", fontSize: "24px" }}>🗺️ Mapping & Geocoding Architecture</h1>
            <p className="hint" style={{ margin: 0 }}>
              Dual-provider management for Mapbox and Google Maps Platform with automatic failover, health tracking, and encrypted credentials.
            </p>
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              type="button"
              onClick={handleOpenAdd}
              style={{ width: "auto", padding: "10px 18px", fontWeight: 700 }}
            >
              ➕ Add Credential
            </button>
            <button
              type="button"
              className="secondary"
              onClick={loadData}
              style={{ width: "auto", padding: "10px 16px" }}
            >
              🔄 Refresh
            </button>
          </div>
        </div>

        {/* Feedback Banners */}
        {error && (
          <div style={{ marginBottom: "16px", padding: "12px", background: "rgba(239, 68, 68, 0.15)", border: "1px solid #ef4444", borderRadius: "8px", color: "#ef4444" }}>
            {error}
          </div>
        )}
        {message && (
          <div
            style={{
              marginBottom: "16px",
              padding: "12px",
              background: message.type === "success" ? "rgba(16, 185, 129, 0.15)" : "rgba(239, 68, 68, 0.15)",
              border: `1px solid ${message.type === "success" ? "#10b981" : "#ef4444"}`,
              borderRadius: "8px",
              color: message.type === "success" ? "#10b981" : "#ef4444",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <span>{message.text}</span>
            <button
              type="button"
              onClick={() => setMessage(null)}
              style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", fontSize: "16px", padding: 0 }}
            >
              ✕
            </button>
          </div>
        )}

        {/* SECTION 1: SYSTEM HEALTH OVERVIEW */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px", marginBottom: "24px" }}>
          <div className="delivery-card" style={{ background: "var(--card-bg, #1a2234)", border: "1px solid var(--border-color, #2d3748)", borderRadius: "12px", padding: "16px" }}>
            <div style={{ fontSize: "13px", color: "#9ca3af", marginBottom: "6px" }}>Google Maps Platform</div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "18px", fontWeight: 700 }}>
                {credentials.filter((c) => c.provider === "google").length} Account(s)
              </span>
              {getStatusBadge(credentials.find((c) => c.provider === "google" && c.isActive)?.health)}
            </div>
            <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "8px" }}>
              Active: {credentials.find((c) => c.provider === "google" && c.isActive)?.name || "None"}
            </div>
          </div>

          <div className="delivery-card" style={{ background: "var(--card-bg, #1a2234)", border: "1px solid var(--border-color, #2d3748)", borderRadius: "12px", padding: "16px" }}>
            <div style={{ fontSize: "13px", color: "#9ca3af", marginBottom: "6px" }}>Mapbox API</div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "18px", fontWeight: 700 }}>
                {credentials.filter((c) => c.provider === "mapbox").length} Account(s)
              </span>
              {getStatusBadge(credentials.find((c) => c.provider === "mapbox" && c.isActive)?.health)}
            </div>
            <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "8px" }}>
              Active: {credentials.find((c) => c.provider === "mapbox" && c.isActive)?.name || "None"}
            </div>
          </div>

          <div className="delivery-card" style={{ background: "var(--card-bg, #1a2234)", border: "1px solid var(--border-color, #2d3748)", borderRadius: "12px", padding: "16px" }}>
            <div style={{ fontSize: "13px", color: "#9ca3af", marginBottom: "6px" }}>In-Memory Query Cache</div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "18px", fontWeight: 700 }}>
                {healthData?.cacheStats?.size || 0} entries
              </span>
              <span style={{ fontSize: "12px", color: "#10b981", fontWeight: 600 }}>Active</span>
            </div>
            <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "8px" }}>
              Hits: {healthData?.cacheStats?.hits || 0} | Misses: {healthData?.cacheStats?.misses || 0}
            </div>
          </div>
        </div>

        {/* SECTION 2: SERVICE ROUTING & FAILOVER RULES */}
        <div className="delivery-card" style={{ background: "var(--card-bg, #1a2234)", border: "1px solid var(--border-color, #2d3748)", borderRadius: "12px", padding: "20px", marginBottom: "24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "10px" }}>
            <div>
              <h3 style={{ margin: "0 0 4px", fontSize: "18px" }}>⚙️ Service Routing & Failover Matrix</h3>
              <p className="hint" style={{ margin: 0 }}>Configure primary and fallback accounts for each map service independently.</p>
            </div>
            <button
              type="button"
              disabled={savingConfig}
              onClick={handleSaveConfig}
              style={{ width: "auto", padding: "8px 20px" }}
            >
              {savingConfig ? "Saving..." : "💾 Save Routing Matrix"}
            </button>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border-color, #2d3748)", textAlign: "left", color: "#9ca3af" }}>
                  <th style={{ padding: "10px 8px" }}>Service</th>
                  <th style={{ padding: "10px 8px" }}>Primary Provider</th>
                  <th style={{ padding: "10px 8px" }}>Fallback Provider</th>
                  <th style={{ padding: "10px 8px", textAlign: "center" }}>Auto Failover</th>
                </tr>
              </thead>
              <tbody>
                {servicesList.map((srv) => {
                  const srvConfig = config?.services?.[srv.key] || {};
                  const primaryId = srvConfig.primary?._id || srvConfig.primary || "";
                  const fallbackId = srvConfig.fallback?._id || srvConfig.fallback || "";

                  return (
                    <tr key={srv.key} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                      <td style={{ padding: "12px 8px" }}>
                        <div style={{ fontWeight: 600 }}>{srv.label}</div>
                        <div style={{ fontSize: "12px", color: "#6b7280" }}>{srv.desc}</div>
                      </td>
                      <td style={{ padding: "12px 8px" }}>
                        <select
                          value={primaryId}
                          onChange={(e) => handleServiceChange(srv.key, "primary", e.target.value)}
                          style={{ width: "100%", padding: "6px 10px", borderRadius: "6px" }}
                        >
                          <option value="">-- Haversine / Offline --</option>
                          {credentials.filter((c) => c.isEnabled).map((c) => (
                            <option key={c._id} value={c._id}>
                              [{c.provider.toUpperCase()}] {c.name} {c.isActive ? "(Active)" : ""}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: "12px 8px" }}>
                        <select
                          value={fallbackId}
                          onChange={(e) => handleServiceChange(srv.key, "fallback", e.target.value)}
                          style={{ width: "100%", padding: "6px 10px", borderRadius: "6px" }}
                        >
                          <option value="">-- Haversine / Offline --</option>
                          {credentials.filter((c) => c.isEnabled).map((c) => (
                            <option key={c._id} value={c._id}>
                              [{c.provider.toUpperCase()}] {c.name} {c.isActive ? "(Active)" : ""}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: "12px 8px", textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={srvConfig.autoFailover !== false}
                          onChange={(e) => handleServiceChange(srv.key, "autoFailover", e.target.checked)}
                          style={{ transform: "scale(1.2)", cursor: "pointer" }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Global Threshold Settings */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", marginTop: "20px", paddingTop: "16px", borderTop: "1px solid var(--border-color, #2d3748)" }}>
            <div>
              <label style={{ fontSize: "13px", fontWeight: 600, display: "block", marginBottom: "4px" }}>
                Failover Threshold
              </label>
              <input
                type="number"
                min="1"
                max="20"
                value={config?.failoverThreshold ?? 3}
                onChange={(e) => setConfig((prev) => ({ ...prev, failoverThreshold: Number(e.target.value) }))}
                style={{ width: "100%", padding: "6px 10px", borderRadius: "6px" }}
              />
              <span className="hint" style={{ fontSize: "11px" }}>Consecutive failures before cooldown</span>
            </div>
            <div>
              <label style={{ fontSize: "13px", fontWeight: 600, display: "block", marginBottom: "4px" }}>
                Cooldown Period (Minutes)
              </label>
              <input
                type="number"
                min="1"
                max="1440"
                value={config?.cooldownMinutes ?? 10}
                onChange={(e) => setConfig((prev) => ({ ...prev, cooldownMinutes: Number(e.target.value) }))}
                style={{ width: "100%", padding: "6px 10px", borderRadius: "6px" }}
              />
              <span className="hint" style={{ fontSize: "11px" }}>Duration credential is sidelined</span>
            </div>
            <div>
              <label style={{ fontSize: "13px", fontWeight: 600, display: "block", marginBottom: "4px" }}>
                Recovery Check Interval
              </label>
              <input
                type="number"
                min="1"
                max="60"
                value={config?.recoveryCheckMinutes ?? 5}
                onChange={(e) => setConfig((prev) => ({ ...prev, recoveryCheckMinutes: Number(e.target.value) }))}
                style={{ width: "100%", padding: "6px 10px", borderRadius: "6px" }}
              />
              <span className="hint" style={{ fontSize: "11px" }}>How often scheduler probes offline keys</span>
            </div>
          </div>
        </div>

        {/* SECTION 3: CREDENTIALS MANAGEMENT */}
        <div className="delivery-card" style={{ background: "var(--card-bg, #1a2234)", border: "1px solid var(--border-color, #2d3748)", borderRadius: "12px", padding: "20px", marginBottom: "24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <div>
              <h3 style={{ margin: "0 0 4px", fontSize: "18px" }}>🔑 Stored API Credentials</h3>
              <p className="hint" style={{ margin: 0 }}>Encrypted at rest with AES-256-CBC. Raw keys are never exposed in browser or logs.</p>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {credentials.length === 0 ? (
              <div style={{ textAlign: "center", padding: "30px", color: "#9ca3af" }}>
                No credentials configured yet. Click "Add Credential" above to register your Mapbox or Google API keys.
              </div>
            ) : (
              credentials.map((cred) => {
                const testResult = testResults[cred._id];
                return (
                  <div
                    key={cred._id}
                    style={{
                      border: "1px solid var(--border-color, #2d3748)",
                      borderRadius: "8px",
                      padding: "16px",
                      background: "rgba(0,0,0,0.15)",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "12px"
                    }}
                  >
                    <div style={{ minWidth: "220px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                        <span
                          style={{
                            padding: "2px 6px",
                            borderRadius: "4px",
                            fontSize: "11px",
                            fontWeight: 700,
                            background: cred.provider === "google" ? "#ea4335" : "#3b82f6",
                            color: "#fff"
                          }}
                        >
                          {cred.provider.toUpperCase()}
                        </span>
                        <strong style={{ fontSize: "15px" }}>{cred.name}</strong>
                        {cred.isActive && (
                          <span style={{ fontSize: "11px", padding: "2px 6px", background: "rgba(16, 185, 129, 0.2)", color: "#10b981", borderRadius: "4px" }}>
                            ★ Active
                          </span>
                        )}
                        {!cred.isEnabled && (
                          <span style={{ fontSize: "11px", padding: "2px 6px", background: "rgba(239, 68, 68, 0.2)", color: "#ef4444", borderRadius: "4px" }}>
                            Disabled
                          </span>
                        )}
                      </div>

                      <div style={{ fontSize: "13px", color: "#9ca3af", display: "flex", gap: "12px", alignItems: "center" }}>
                        <code>{cred.maskedToken}</code>
                        <span>•</span>
                        <span>Env: {cred.environment}</span>
                        <span>•</span>
                        <span>Type: {cred.credentialType}</span>
                      </div>

                      {cred.health && (
                        <div style={{ fontSize: "12px", marginTop: "6px", display: "flex", gap: "10px", alignItems: "center" }}>
                          {getStatusBadge(cred.health)}
                          {cred.health.avgResponseMs && (
                            <span style={{ color: "#9ca3af" }}>~{cred.health.avgResponseMs}ms</span>
                          )}
                          {cred.health.lastError && (
                            <span style={{ color: "#ef4444", maxWidth: "350px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={cred.health.lastError}>
                              ⚠ {cred.health.lastError}
                            </span>
                          )}
                        </div>
                      )}

                      {testResult && (
                        <div style={{ marginTop: "6px", fontSize: "12px", color: testResult.ok ? "#10b981" : "#ef4444" }}>
                          Probe: {testResult.ok ? `Success (${testResult.responseMs}ms)` : `Failed: ${testResult.error}`}
                        </div>
                      )}
                    </div>

                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                      <button
                        type="button"
                        className="secondary"
                        disabled={testingId === cred._id}
                        onClick={() => handleTest(cred._id)}
                        style={{ width: "auto", padding: "6px 12px", fontSize: "12px" }}
                      >
                        {testingId === cred._id ? "Testing..." : "⚡ Test Probe"}
                      </button>

                      {!cred.isActive && (
                        <button
                          type="button"
                          className="secondary"
                          onClick={() => handleActivate(cred._id, cred.name)}
                          style={{ width: "auto", padding: "6px 12px", fontSize: "12px" }}
                        >
                          Set Active
                        </button>
                      )}

                      <button
                        type="button"
                        className="secondary"
                        onClick={() => handleOpenEdit(cred)}
                        style={{ width: "auto", padding: "6px 12px", fontSize: "12px" }}
                      >
                        Edit
                      </button>

                      <button
                        type="button"
                        className="secondary"
                        disabled={cred.isActive}
                        onClick={() => handleDelete(cred._id, cred.name)}
                        style={{ width: "auto", padding: "6px 12px", fontSize: "12px", color: "#ef4444" }}
                        title={cred.isActive ? "Deactivate before deleting" : "Delete"}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* SECTION 4: MAP SUBSCRIPTION & BILLING */}
        <div className="delivery-card" style={{ background: "var(--card-bg, #1a2234)", border: "1px solid var(--border-color, #2d3748)", borderRadius: "12px", padding: "20px", marginBottom: "24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "10px" }}>
            <div>
              <h3 style={{ margin: "0 0 4px", fontSize: "18px" }}>💳 Map Subscription & Billing</h3>
              <p className="hint" style={{ margin: 0 }}>Track active API plans, usage estimates, and access billing portals for each provider.</p>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "16px", marginBottom: "20px" }}>
            {/* Mapbox Billing Card */}
            <div style={{ border: "1px solid rgba(59,130,246,0.3)", borderRadius: "10px", padding: "16px", background: "rgba(59,130,246,0.06)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
                <span style={{ fontSize: "20px" }}>🗺️</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: "15px" }}>Mapbox</div>
                  <div style={{ fontSize: "12px", color: "#6b7280" }}>
                    {credentials.filter(c => c.provider === "mapbox").length} credential(s) registered
                  </div>
                </div>
                <span style={{
                  marginLeft: "auto",
                  padding: "3px 8px",
                  borderRadius: "6px",
                  fontSize: "11px",
                  fontWeight: 700,
                  background: credentials.some(c => c.provider === "mapbox" && c.isActive) ? "rgba(16,185,129,0.2)" : "rgba(156,163,175,0.2)",
                  color: credentials.some(c => c.provider === "mapbox" && c.isActive) ? "#10b981" : "#9ca3af"
                }}>
                  {credentials.some(c => c.provider === "mapbox" && c.isActive) ? "● Active" : "○ Inactive"}
                </span>
              </div>

              <div style={{ fontSize: "13px", display: "flex", flexDirection: "column", gap: "6px", marginBottom: "14px" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "#9ca3af" }}>Free tier</span>
                  <span style={{ fontWeight: 600 }}>50,000 map loads/mo</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "#9ca3af" }}>Geocoding API</span>
                  <span style={{ fontWeight: 600 }}>100,000 requests/mo</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "#9ca3af" }}>Directions API</span>
                  <span style={{ fontWeight: 600 }}>100,000 requests/mo</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", paddingTop: "6px", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                  <span style={{ color: "#9ca3af" }}>Billing Portal</span>
                  <a href="https://account.mapbox.com/billing/" target="_blank" rel="noopener noreferrer"
                    style={{ color: "#60a5fa", fontSize: "12px", textDecoration: "none", fontWeight: 600 }}>
                    Open Dashboard ↗
                  </a>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "#9ca3af" }}>Usage Dashboard</span>
                  <a href="https://account.mapbox.com/statistics/" target="_blank" rel="noopener noreferrer"
                    style={{ color: "#60a5fa", fontSize: "12px", textDecoration: "none", fontWeight: 600 }}>
                    View Stats ↗
                  </a>
                </div>
              </div>

              <div style={{ fontSize: "11px", color: "#6b7280", background: "rgba(0,0,0,0.2)", borderRadius: "6px", padding: "8px" }}>
                💡 Mapbox bills on actual API usage. Monitor the Usage Dashboard to avoid unexpected overages above the free tier.
              </div>
            </div>

            {/* Google Maps Platform Billing Card */}
            <div style={{ border: "1px solid rgba(234,67,53,0.3)", borderRadius: "10px", padding: "16px", background: "rgba(234,67,53,0.06)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
                <span style={{ fontSize: "20px" }}>🌍</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: "15px" }}>Google Maps Platform</div>
                  <div style={{ fontSize: "12px", color: "#6b7280" }}>
                    {credentials.filter(c => c.provider === "google").length} credential(s) registered
                  </div>
                </div>
                <span style={{
                  marginLeft: "auto",
                  padding: "3px 8px",
                  borderRadius: "6px",
                  fontSize: "11px",
                  fontWeight: 700,
                  background: credentials.some(c => c.provider === "google" && c.isActive) ? "rgba(16,185,129,0.2)" : "rgba(156,163,175,0.2)",
                  color: credentials.some(c => c.provider === "google" && c.isActive) ? "#10b981" : "#9ca3af"
                }}>
                  {credentials.some(c => c.provider === "google" && c.isActive) ? "● Active" : "○ Inactive"}
                </span>
              </div>

              <div style={{ fontSize: "13px", display: "flex", flexDirection: "column", gap: "6px", marginBottom: "14px" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "#9ca3af" }}>Monthly credit</span>
                  <span style={{ fontWeight: 600 }}>$200 free / month</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "#9ca3af" }}>Geocoding API</span>
                  <span style={{ fontWeight: 600 }}>~40,000 requests free</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "#9ca3af" }}>Directions API</span>
                  <span style={{ fontWeight: 600 }}>~40,000 requests free</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", paddingTop: "6px", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                  <span style={{ color: "#9ca3af" }}>GCP Console</span>
                  <a href="https://console.cloud.google.com/billing" target="_blank" rel="noopener noreferrer"
                    style={{ color: "#f87171", fontSize: "12px", textDecoration: "none", fontWeight: 600 }}>
                    Open Billing ↗
                  </a>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "#9ca3af" }}>Maps Usage</span>
                  <a href="https://console.cloud.google.com/google/maps-apis/metrics" target="_blank" rel="noopener noreferrer"
                    style={{ color: "#f87171", fontSize: "12px", textDecoration: "none", fontWeight: 600 }}>
                    View Metrics ↗
                  </a>
                </div>
              </div>

              <div style={{ fontSize: "11px", color: "#6b7280", background: "rgba(0,0,0,0.2)", borderRadius: "6px", padding: "8px" }}>
                💡 Google charges per request above the $200 credit. Set a budget alert in GCP Console to get notified before hitting paid usage.
              </div>
            </div>
          </div>

          {/* Budget Alert Reminder */}
          <div style={{ border: "1px solid rgba(245,158,11,0.3)", borderRadius: "8px", padding: "12px 16px", background: "rgba(245,158,11,0.06)", display: "flex", alignItems: "flex-start", gap: "10px" }}>
            <span style={{ fontSize: "18px", flexShrink: 0 }}>⚠️</span>
            <div style={{ fontSize: "13px" }}>
              <strong>Set budget alerts</strong> — Configure spending limits and alerts in both providers' consoles to avoid unexpected charges.
              Your failover logic reduces single-provider exposure, but monitoring actual API usage is still recommended.
              <div style={{ marginTop: "8px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
                <a href="https://console.cloud.google.com/billing/budgets" target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: "12px", color: "#fbbf24", fontWeight: 600, textDecoration: "none" }}>
                  Set GCP Budget Alert ↗
                </a>
                <a href="https://account.mapbox.com/billing/spending-limit/" target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: "12px", color: "#fbbf24", fontWeight: 600, textDecoration: "none" }}>
                  Set Mapbox Spending Limit ↗
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 5: AUDIT LOG */}

        <div className="delivery-card" style={{ background: "var(--card-bg, #1a2234)", border: "1px solid var(--border-color, #2d3748)", borderRadius: "12px", padding: "20px" }}>
          <h3 style={{ margin: "0 0 12px", fontSize: "18px" }}>📜 Recent Map Architecture Audit Log</h3>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border-color, #2d3748)", textAlign: "left", color: "#9ca3af" }}>
                  <th style={{ padding: "8px" }}>Timestamp</th>
                  <th style={{ padding: "8px" }}>Actor</th>
                  <th style={{ padding: "8px" }}>Action</th>
                  <th style={{ padding: "8px" }}>Provider</th>
                  <th style={{ padding: "8px" }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ padding: "16px", textAlign: "center", color: "#9ca3af" }}>
                      No audit events recorded yet.
                    </td>
                  </tr>
                ) : (
                  auditLogs.map((log) => (
                    <tr key={log._id} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                      <td style={{ padding: "8px", whiteSpace: "nowrap", color: "#9ca3af" }}>
                        {new Date(log.createdAt).toLocaleString()}
                      </td>
                      <td style={{ padding: "8px", fontWeight: 600 }}>{log.adminName}</td>
                      <td style={{ padding: "8px" }}>
                        <span
                          style={{
                            padding: "2px 6px",
                            borderRadius: "4px",
                            fontSize: "11px",
                            background: "rgba(255,255,255,0.1)"
                          }}
                        >
                          {log.action}
                        </span>
                      </td>
                      <td style={{ padding: "8px" }}>{log.provider?.toUpperCase()}</td>
                      <td style={{ padding: "8px", color: "#cbd5e1" }}>{log.details}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* MODAL: ADD / EDIT CREDENTIAL */}
        {showModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.75)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
              padding: "16px"
            }}
          >
            <div
              style={{
                background: "var(--card-bg, #1a2234)",
                border: "1px solid var(--border-color, #2d3748)",
                borderRadius: "12px",
                width: "100%",
                maxWidth: "520px",
                padding: "24px"
              }}
            >
              <h2 style={{ margin: "0 0 16px", fontSize: "20px" }}>
                {editingId ? "Edit Map Credential" : "Add Map Credential"}
              </h2>

              <form onSubmit={handleSaveCredential} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "4px" }}>
                    Provider
                  </label>
                  <select
                    value={form.provider}
                    disabled={!!editingId}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        provider: e.target.value,
                        credentialType: e.target.value === "mapbox" ? "secret" : "server"
                      }))
                    }
                    style={{ width: "100%", padding: "8px 10px", borderRadius: "6px" }}
                  >
                    <option value="mapbox">Mapbox</option>
                    <option value="google">Google Maps Platform</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "4px" }}>
                    Account / Key Label
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Mapbox Production Lagos"
                    value={form.name}
                    onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                    style={{ width: "100%", padding: "8px 10px", borderRadius: "6px" }}
                  />
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "4px" }}>
                    {editingId ? "Update Secret Token / API Key (leave empty to keep current)" : "Secret Token / API Key"}
                  </label>
                  <input
                    type="password"
                    required={!editingId}
                    placeholder={form.provider === "mapbox" ? "sk.eyJ..." : "AIzaSy..."}
                    value={form.credential}
                    onChange={(e) => setForm((prev) => ({ ...prev, credential: e.target.value }))}
                    style={{ width: "100%", padding: "8px 10px", borderRadius: "6px" }}
                  />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "4px" }}>
                      Key Type
                    </label>
                    <select
                      value={form.credentialType}
                      onChange={(e) => setForm((prev) => ({ ...prev, credentialType: e.target.value }))}
                      style={{ width: "100%", padding: "8px 10px", borderRadius: "6px" }}
                    >
                      {form.provider === "mapbox" ? (
                        <>
                          <option value="secret">Secret Token (sk.*)</option>
                          <option value="public">Public Token (pk.*)</option>
                        </>
                      ) : (
                        <>
                          <option value="server">Server-Restricted Key</option>
                          <option value="unrestricted">Unrestricted Key</option>
                        </>
                      )}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "4px" }}>
                      Environment
                    </label>
                    <select
                      value={form.environment}
                      onChange={(e) => setForm((prev) => ({ ...prev, environment: e.target.value }))}
                      style={{ width: "100%", padding: "8px 10px", borderRadius: "6px" }}
                    >
                      <option value="production">Production</option>
                      <option value="staging">Staging</option>
                      <option value="development">Development</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "4px" }}>
                    Admin Notes (Optional)
                  </label>
                  <textarea
                    rows="2"
                    placeholder="Usage notes, account quota or owner details"
                    value={form.notes}
                    onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                    style={{ width: "100%", padding: "8px 10px", borderRadius: "6px" }}
                  />
                </div>

                <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "13px" }}>
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                    />
                    Set as active account for {form.provider}
                  </label>

                  {editingId && (
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "13px" }}>
                      <input
                        type="checkbox"
                        checked={form.isEnabled}
                        onChange={(e) => setForm((prev) => ({ ...prev, isEnabled: e.target.checked }))}
                      />
                      Enabled
                    </label>
                  )}
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "16px" }}>
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => setShowModal(false)}
                    style={{ width: "auto", padding: "8px 16px" }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{ width: "auto", padding: "8px 20px" }}
                  >
                    {editingId ? "Update" : "Save Credential"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
